/**
 * Onboarding gate — the production seam that wires the post-login onboarding
 * machinery into the live authenticated portal entry (contestant-onboarding-intake
 * spec).
 *
 * `OnboardingGate` is a thin route-element wrapper that sits *between* the
 * existing `ProtectedRoute` access gate (which keeps `canSeePortal` as the sole
 * access gate, Req 6.2) and the portal/apply surfaces it protects. It owns three
 * responsibilities and nothing else:
 *
 * 1. **Flag check first (Req 4.1, 4.3, 4.4).** Before any hook that could reach
 *    the transport, the gate checks `isApplicationFormEnabled()`. When the flag
 *    is off it returns `children` unchanged — no `OnboardingRouter` is mounted,
 *    so `getOnboardingDestination` / `submitContactIntake` are never called and
 *    the Legacy_Portal_Landing behavior is preserved byte-for-byte.
 * 2. **Block on resolution (Req 1.1, 1.2, 1.3).** When the flag is on, the gate
 *    mounts `<OnboardingRouter/>`, which resolves the destination before any
 *    child route renders: it shows its own `role="status"` loading region while
 *    resolving, its `role="alert"` retry region on error/timeout/unknown, and the
 *    blocking `ContactIntakeForm` inline for `CONTACT_INTAKE`. While the router is
 *    in any of those states the gate renders **none** of `children`.
 * 3. **Map + navigate (Req 1.4, 1.5, 3.x, 5.2).** For every resolved non-intake
 *    destination the router hands the gate a `ResolvedDestination`. The gate maps
 *    it to a concrete existing route via `mapDestinationToPath` and either renders
 *    `children` in place (already at the target — the loop guard) or issues a
 *    single `<Navigate replace/>`. Once it has cleared to the target it keeps
 *    rendering `children` for the rest of the session without re-resolving, so a
 *    completed Contestant can navigate Portal_Entry child routes freely (Req 5.2).
 *
 * Because React Router keeps the parent route element mounted while the user
 * navigates between its child routes, mounting the gate as the `/portal` element
 * means resolution runs once on portal entry and child-route navigation is not
 * re-gated.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 5.2
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { isApplicationFormEnabled } from '@/config/featureFlags';
import AuthContext from '@/context/AuthContext';
import { mapDestinationToPath } from '@/lib/application-form/onboardingNav';

import { OnboardingRouter } from './OnboardingRouter';
import type { ResolvedDestination } from './OnboardingRouter';

export interface OnboardingGateProps {
  /**
   * The surface this gate protects, rendered once onboarding has cleared (or
   * immediately when the flag is off). For `/portal` this is `<PortalLayout/>`
   * (whose `<Outlet/>` renders the child route); for `/apply` this is the
   * `<ApplicationFlow/>` surface.
   */
  children: React.ReactNode;
}

/**
 * Builds the navigation target string for a mapped destination, appending the
 * resolved `competitionId` as a `?competitionId=` query param when the kind
 * carries one (e.g. `/apply?competitionId=<id>`) so the application surface can
 * bind to the exact competition the decision resolved against.
 */
function toNavPath(path: string, competitionId?: string): string {
  if (!competitionId) return path;
  return `${path}?competitionId=${encodeURIComponent(competitionId)}`;
}

/**
 * The flag-on body of the gate. Split into its own component so the flag check
 * in `OnboardingGate` happens *before* any hook runs — when the flag is off no
 * onboarding hook (and therefore no transport call) is ever reached (Req 4.3,
 * 4.4).
 */
function OnboardingGateEnabled({ children }: OnboardingGateProps): React.ReactElement {
  const location = useLocation();

  // Read the authenticated user defensively: `useContext` returns null when no
  // AuthProvider is mounted (e.g. in isolated component tests), so this never
  // throws the way `useAuth()` would. The first/last name the contestant already
  // entered on the sign-up screen (Cognito given_name/family_name → user.first/
  // lastName) is forwarded as the intake form's initial values so they are not
  // re-typed.
  const auth = useContext(AuthContext);
  const intakeInitialValues: { legalFirstName?: string; legalLastName?: string; primaryEmail?: string } = {};
  if (auth?.user?.firstName) intakeInitialValues.legalFirstName = auth.user.firstName;
  if (auth?.user?.lastName) intakeInitialValues.legalLastName = auth.user.lastName;
  if (auth?.user?.email) intakeInitialValues.primaryEmail = auth.user.email;

  // The resolved non-intake destination, captured once from the router's
  // onResolve. A single resolution drives every subsequent render (Req 5.2).
  const [resolved, setResolved] = useState<ResolvedDestination | null>(null);

  // Latches true once the gate has reached its target route. From then on it
  // renders `children` regardless of which child route the Contestant navigates
  // to, so a cleared gate never bounces them back to the landing route
  // (Req 5.2 — no re-prompting / no trap for the rest of the session).
  const [cleared, setCleared] = useState(false);

  // Fail-open latch: set when onboarding resolution terminally fails (the
  // backend is unavailable / erroring — e.g. the live resolver on the deployed
  // dev build). Rather than trap the Contestant on the router's retry screen (or
  // white-screen if anything downstream throws), we proceed into the protected
  // surface so an unreachable onboarding backend can never block portal access.
  const [failedOpen, setFailedOpen] = useState(false);
  const handleUnresolvable = useCallback((error: Error) => {
    console.error('[OnboardingGate] Onboarding resolution failed; failing open to portal:', error);
    setFailedOpen(true);
  }, []);

  // Set true when the router shows the inline Contact_Intake_Form this session
  // (via `onIntakePending`). It distinguishes a member who JUST completed intake
  // — who should be forwarded to the dashboard (Req 1.1, 1.3) — from one who was
  // ALREADY onboarded, who must be left on the surface they intentionally
  // navigated to (see the DASHBOARD pass-through below).
  const intakeShownRef = React.useRef(false);
  const handleIntakePending = useCallback(() => {
    intakeShownRef.current = true;
  }, []);

  const handleResolve = useCallback((destination: ResolvedDestination) => {
    setResolved(destination);
  }, []);

  const target = resolved ? mapDestinationToPath(resolved) : null;
  const atTarget = target !== null && location.pathname === target.path;

  // Once we are at the target route, latch `cleared` so subsequent child-route
  // navigation keeps rendering `children` instead of re-issuing a Navigate back
  // to the target (Req 5.2).
  useEffect(() => {
    if (atTarget && !cleared) setCleared(true);
  }, [atTarget, cleared]);

  // Onboarding resolution terminally failed → fail open to the protected
  // surface (never block portal access on an unreachable onboarding backend).
  if (failedOpen) {
    return <>{children}</>;
  }

  // Still resolving / erroring / collecting contact intake: the router owns the
  // status and inline ContactIntakeForm surfaces, and we render none of
  // `children` until a non-intake destination resolves (Req 1.2, 1.3, 1.6, 1.7).
  // On a terminal resolution failure the router signals `onUnresolvable`, which
  // flips `failedOpen` above so we proceed into the portal rather than leaving
  // the Contestant on a retry screen.
  if (!resolved || target === null) {
    return (
      <OnboardingRouter
        onResolve={handleResolve}
        onIntakePending={handleIntakePending}
        onUnresolvable={handleUnresolvable}
        intakeInitialValues={intakeInitialValues}
      />
    );
  }

  // Cleared earlier this session, or already standing on the target route: the
  // loop guard — render the wrapped surface in place rather than navigating
  // (Req 1.4, 1.5, 5.2).
  if (cleared || atTarget) {
    return <>{children}</>;
  }

  // Already-onboarded pass-through (member-onboarding-flow §A). Under the
  // narrowed routing the only post-intake destination is `DASHBOARD`, which maps
  // to `/portal`. When the gate never showed the inline Contact_Intake_Form this
  // session (`!intakeShownRef`), the member was already onboarded and
  // intentionally navigated to this surface — e.g. `/apply`, whose OWN
  // `IntakeWindowGate` handles window-gating (§B). The `DASHBOARD` default must
  // NOT pull them off it back to `/portal`; render the surface in place. This is
  // what makes `/apply` reachable post-intake and fixes the dashboard
  // "Apply for <season>" → `/apply` → `/portal` bounce. The post-intake forward
  // below still runs when the member DID just complete the inline intake form
  // (`intakeShownRef`), satisfying "no full-screen form between intake and the
  // dashboard" (Req 1.1, 1.3).
  if (resolved.kind === 'DASHBOARD' && !intakeShownRef.current) {
    return <>{children}</>;
  }

  // Resolved to a route we are not on yet: forward there once (Req 1.4, 1.5,
  // 3.1–3.4).
  return <Navigate to={toNavPath(target.path, target.competitionId)} replace />;
}

/**
 * The onboarding gate. Checks the Application_Form_Flag first (Req 4.1) and,
 * only when enabled, delegates to the flag-on body that mounts the
 * `OnboardingRouter`.
 */
export function OnboardingGate({ children }: OnboardingGateProps): React.ReactElement {
  // Flag off (Req 4.1, 4.3, 4.4): structurally transparent — render the legacy
  // surface before any hook that could call the transport. No OnboardingRouter
  // is mounted, so getOnboardingDestination / submitContactIntake are unreachable.
  if (!isApplicationFormEnabled()) {
    return <>{children}</>;
  }

  return <OnboardingGateEnabled>{children}</OnboardingGateEnabled>;
}

export default OnboardingGate;
